/**
 * A medication's strength as people pick or type it. RxTerms lists each drug's
 * strengths ("325-5 mg Tab", "70-2,800 var units Tab"); the add form offers
 * them as options. A combination has more than one number, which the numeric
 * dose column can't hold, so the strength is saved as text; a single amount
 * also fills dose + unit so everything that reads those keeps working.
 */

export interface StrengthOption {
  label: string;
  rxcui: string | null;
}

const FORM_WORDS: Record<string, string> = { Tab: 'tablet', Cap: 'capsule', Sol: 'solution', Susp: 'suspension' };

/** "325-5 mg Tab" → "325 / 5 mg tablet"; "70-2,800 var units Tab" → "70 / 2,800 units tablet". */
export function formatStrength(raw: string): string {
  return String(raw || '')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/(\d[\d,.]*)-(\d[\d,.]*)/g, '$1 / $2')
    .replace(/\bvar units\b/g, 'units')
    .replace(/\b(Tab|Cap|Sol|Susp)\b/g, (word) => FORM_WORDS[word]);
}

/** The options for one RxTerms result: its strengths, each with its RxCUI. */
export function strengthOptions(strengths: string[] = [], rxcuis: string[] = []): StrengthOption[] {
  return strengths
    .map((raw, i) => ({ label: formatStrength(raw), rxcui: rxcuis[i] ? String(rxcuis[i]) : null }))
    .filter((option) => option.label);
}

/**
 * The dose and unit a single-amount strength stands for: "500 mg tablet" →
 * 500 mg, "2 tablets" → 2 tablets. A combination ("325 / 5 mg") or a
 * concentration ("5 mg/ml") has none: its strength stays text only.
 */
export function amountFromStrength(strength: string): { dose: number; unit: string | null } | null {
  const text = String(strength || '').trim();
  if (!text || text.includes('/')) return null;
  const match = /^(\d[\d,]*(?:\.\d+)?)\s*([A-Za-zµ%]+)?/.exec(text);
  if (!match) return null;
  const dose = Number(match[1].replace(/,/g, ''));
  return Number.isFinite(dose) ? { dose, unit: match[2] || null } : null;
}

/**
 * A dose's display text, or '' if none was entered. The medications table
 * defaults an omitted dose to 0, not null (see backend/api/records/utilities/
 * medication-strength.js), so every place that reads a stored dose has to
 * treat 0 — as a number or as "0"/"0.0" text — the same as no dose at all.
 */
export function doseText(dose: unknown): string {
  if (dose == null) return '';
  const text = String(dose).trim();
  if (!text) return '';
  const num = Number(text);
  return Number.isFinite(num) && num === 0 ? '' : text;
}

/** What to show for a medication's strength: its own, else its dose and unit. */
export function strengthOf(med: { strength?: string | null; dose?: unknown; unit?: string | null }): string {
  if (med.strength && String(med.strength).trim()) return String(med.strength).trim();
  const dose = doseText(med.dose);
  return dose ? [dose, (med.unit || '').trim()].filter(Boolean).join(' ') : '';
}

/** Keep a picked drug identity in step with strength edits, including clearing it. */
export function medicationStrengthPayload(
  form: { strength: string; rxcui: string },
  existing?: { strength?: string | null; dose?: unknown; unit?: string | null; rxcui?: string | null } | null,
): { strength?: string | null; dose?: number | null; unit?: string | null; rxcui?: string | null } {
  const payload: ReturnType<typeof medicationStrengthPayload> = {};
  const strength = form.strength.trim();
  const strengthChanged = strength !== (existing ? strengthOf(existing) : '');
  if (strengthChanged) {
    const amount = amountFromStrength(strength);
    payload.strength = strength || null;
    payload.dose = amount?.dose ?? null;
    payload.unit = amount?.unit ?? null;
  }
  const rxcui = form.rxcui || null;
  if (strengthChanged || rxcui !== (existing?.rxcui || null)) payload.rxcui = rxcui;
  return payload;
}
